import { createColumnHelper } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';

import { WorkflowsArtifactHelm } from '@api/types.gen';

import { useEnvironment } from '@/react/portainer/environments/queries';
import { getDashboardRoute } from '@/react/portainer/environments/utils';

import { Button } from '@@/buttons';
import { Icon } from '@@/Icon';
import { Link } from '@@/Link';
import { Badge } from '@@/Badge';

import { EnvironmentReconcileStatus } from '../types';
import { StatusBadge } from '../../components/StatusBadge';

import { DriftCountButton, DriftModal } from './DriftModal';
import { HelmReleaseModal } from './HelmReleaseModal';
import { ResourceCountButton, ResourcesModal } from './ResourcesModal';

/** One environment's report, with the name resolved for searching and sorting. */
export type ReconcileRow = EnvironmentReconcileStatus & {
  id: number;
  name: string;
  // Carried on the row so the resources modal can ask for the right deployment
  // without the column reaching back up to the artifact.
  workflowId: number;
  deploymentId: number;
  deploymentName: string;
  // Set when the deployment is a chart, which changes where its resources are
  // read from: the environment running the release rather than the bundle.
  helm?: WorkflowsArtifactHelm;
};

const columnHelper = createColumnHelper<ReconcileRow>();

/**
 * Names the artifact a row reports on.
 *
 * Only shown when a workflow has more than one reconciled artifact, because
 * with one it says the same thing on every row.
 */
const artifactColumn = columnHelper.accessor('deploymentName', {
  id: 'artifact',
  header: 'Artifact',
  meta: { width: '16%' },
  cell({ row: { original: environment } }) {
    return <span className="font-medium">{environment.deploymentName}</span>;
  },
});

const columns = [
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Environment',
    meta: { width: '32%' },
    cell({ row: { original: environment } }) {
      return <EnvironmentLink environment={environment} />;
    },
  }),

  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    meta: { width: '12%' },
    cell({ row: { original: environment } }) {
      return (
        <div className="flex items-center gap-2">
          <StatusBadge status={environment.status} />
          {environment.stale && <Badge type="warn">Unreachable</Badge>}
        </div>
      );
    },
  }),

  columnHelper.accessor('revision', {
    id: 'revision',
    header: 'Revision',
    meta: { width: '12%' },
    cell({ row: { original: environment } }) {
      if (!environment.revision) {
        return <span>-</span>;
      }

      return (
        <span className="font-mono text-xs">
          {environment.revision.slice(0, 8)}
        </span>
      );
    },
  }),

  columnHelper.accessor('resourceCount', {
    id: 'resources',
    header: 'Resources',
    meta: { width: '14%' },
    cell({ row: { original: environment, index } }) {
      return <ResourcesCell environment={environment} index={index} />;
    },
  }),

  columnHelper.accessor('message', {
    id: 'details',
    header: 'Details',
    enableSorting: false,
    meta: { width: '30%' },
    cell({ row: { original: environment, index } }) {
      return <DetailsCell environment={environment} index={index} />;
    },
  }),
];

/**
 * The columns for one workflow's reconcile table.
 *
 * Widths are declared rather than left to the browser: Details is empty for a
 * healthy environment and several words for a drifted one, so an auto-sized
 * table moves every column when the status filter changes. They are declared
 * twice for the same reason - adding a column to an auto-sized table would
 * shuffle the rest.
 */
export function reconcileColumns(withArtifact: boolean) {
  if (!withArtifact) {
    return columns;
  }

  const widths = ['26%', '16%', '12%', '10%', '12%', '24%'];

  return [columns[0], artifactColumn, ...columns.slice(1)].map(
    (column, index) => ({ ...column, meta: { width: widths[index] } })
  );
}

/**
 * The resource count, which opens the list of what the deployment declares.
 *
 * The list is the same for every environment, so it is fetched only when asked
 * for rather than carried on each row.
 */
function ResourcesCell({
  environment,
  index,
}: {
  environment: ReconcileRow;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  if (typeof environment.resourceCount !== 'number') {
    return <span>-</span>;
  }

  return (
    <>
      <ResourceCountButton
        count={environment.resourceCount}
        index={index}
        onClick={() => setIsOpen(true)}
      />

      {isOpen &&
        (environment.helm ? (
          <HelmReleaseModal
            environmentId={environment.id}
            helm={environment.helm}
            name={environment.deploymentName}
            onDismiss={() => setIsOpen(false)}
          />
        ) : (
          <ResourcesModal
            workflowId={environment.workflowId}
            deploymentId={environment.deploymentId}
            name={environment.deploymentName}
            onDismiss={() => setIsOpen(false)}
          />
        ))}
    </>
  );
}

/**
 * What an environment has to say beyond its status: a message, the resources
 * that are not working, and what has drifted.
 *
 * Drift opens in a modal and the rest expands in place. A message is one line
 * and the unhealthy resources are only the ones that are not working, but drift
 * carries a patch per change, which is more than a table row can hold.
 */
function DetailsCell({
  environment,
  index,
}: {
  environment: ReconcileRow;
  index: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDriftOpen, setIsDriftOpen] = useState(false);

  const drift = environment.drift ?? [];
  const unhealthy = environment.unhealthy ?? [];

  // Collapsed by default, because environments that fail together fail with the
  // same message: expanded, one broken deployment becomes pages of identical
  // text.
  const canExpand = !!environment.message || unhealthy.length > 0;

  if (!canExpand && !drift.length) {
    return <span>-</span>;
  }

  return (
    // The column carries the width, so an expanded message wraps inside it
    // rather than sizing the column to itself.
    <div className="flex w-full flex-col gap-1">
      {canExpand && (
        <Button
          color="none"
          // .btn centres its content and this one stretches to the cell, which
          // leaves the chevron floating in the middle of the column.
          className="flex cursor-pointer !justify-start self-start !p-0 text-left"
          onClick={() => setIsExpanded(!isExpanded)}
          data-cy={`workflow-reconcile-details-${index}`}
        >
          <div className="pt-0.5 pr-1">
            <Icon
              icon={ChevronRight}
              className={clsx('transition-transform', {
                'rotate-90': isExpanded,
              })}
            />
          </div>
          <div
            className={clsx('overflow-hidden break-words whitespace-normal', {
              'line-clamp-1': !isExpanded,
            })}
          >
            {environment.message || 'View details'}
          </div>
        </Button>
      )}

      {isExpanded &&
        unhealthy.map((resource) => (
          <span key={resource.id} className="text-xs text-error-7">
            {resource.id}: {resource.status}
            {resource.message ? ` — ${resource.message}` : ''}
          </span>
        ))}

      {!!drift.length && (
        <DriftCountButton
          count={drift.length}
          index={index}
          onClick={() => setIsDriftOpen(true)}
        />
      )}

      {isDriftOpen && (
        <DriftModal
          name={environment.name}
          drift={drift}
          onDismiss={() => setIsDriftOpen(false)}
        />
      )}
    </div>
  );
}

function EnvironmentLink({ environment }: { environment: ReconcileRow }) {
  const { data } = useEnvironment(environment.id);

  if (!data) {
    return <span>{environment.name}</span>;
  }

  const { to, params } = getDashboardRoute(data);

  return (
    <Link
      to={to}
      params={params}
      data-cy={`workflow-reconcile-environment-${environment.id}`}
    >
      {environment.name}
    </Link>
  );
}
